/**
 * Статистика командных игр банды (docs/team-stats-api.md).
 *
 * Бэкенд отдаёт только машинные ключи режимов и коды ошибок — подписи,
 * иконки и переходы на родные страницы раундов собираются здесь.
 */

import { Bomb, Crown, FileSignature, Package, Rocket, Swords, TrendingUp, CircleDot, Disc3 } from 'lucide-react'
import { getGameLabel } from '../../profile/utils/profileFormat'
import { raritySurfaceStyle } from '../../../utils/rarity'

/** Все 8 режимов в том же порядке, что в блоке `modes` до сортировки по обороту */
export const MODE_KINDS = ['case', 'upgrader', 'contract', 'crash', 'classic', 'double', 'wheel50', 'casebattle', 'mines']

const resolveModeKind = (value) => {
  if (typeof value === 'string') return value
  return value?.kind ?? value?.mode ?? value?.game ?? value?.ref?.game ?? null
}

/**
 * Подпись режима. Переиспользуем уже переведённые на 9 языков ключи
 * `publicProfile.games.*` — там те же 8 значений, свои дубли не заводим.
 */
export const modeLabel = (value, t) => {
  const kind = resolveModeKind(value)
  if (kind === 'mines') return 'Mines'
  return kind ? getGameLabel(kind, t) : t('team.stats.gameTitle', 'Командная игра')
}

/** Иконка режима: одна на весь модуль статистики (чипы фильтра, строки, диаграмма) */
export const MODE_ICONS = {
  case: Package,
  upgrader: TrendingUp,
  contract: FileSignature,
  crash: Rocket,
  classic: Crown,
  double: CircleDot,
  wheel50: Disc3,
  casebattle: Swords,
  mines: Bomb
}

export const modeIcon = (value) => MODE_ICONS[resolveModeKind(value)] || Package

/** Периоды переключателя: сутки по МСК, 7/30 дней и всё время */
export const PERIODS = ['today', 'yesterday', '7', '30', 'all']

export const periodLabel = (period, t) => {
  const labels = {
    today: t('team.stats.periodToday', 'Сегодня'),
    yesterday: t('team.stats.periodYesterday', 'Вчера'),
    7: t('team.stats.period7', '7 дней'),
    30: t('team.stats.period30', '30 дней'),
    all: t('team.stats.periodAll', 'Всё время')
  }
  return labels[period] || period
}

export const emptyDayText = (period, t) => period === 'today'
  ? t('team.stats.emptyToday', 'Сегодня командных игр ещё не было')
  : t('team.stats.emptyYesterday', 'Вчера командных игр не было')

/**
 * Куда вести по нажатию на раунд: `ref.game` → уже существующая страница
 * карточки раунда. У сессии `ref: null` — там свой экран детализации.
 */
const ROUND_ROUTES = {
  classic: (id) => `/royale/history/${id}`,
  double: (id) => `/double/history/${id}`,
  wheel50: (id) => `/x50/history/${id}`,
  crash: (id) => `/crash/history/${id}`,
  casebattle: (id) => `/battles/${id}`
}

export const roundPath = (ref) => {
  if (!ref?.game || ref.id == null) return null
  const build = ROUND_ROUTES[ref.game]
  return build ? build(ref.id) : null
}

/**
 * Подложка предмета цветом редкости. В сессиях `color` приходит с решёткой
 * (`"#8847ff"`), а общий helper ждёт hex без неё — нормализуем здесь.
 */
export const itemSurfaceStyle = (item) => raritySurfaceStyle({
  color: typeof item?.color === 'string' ? item.color.replace('#', '') : item?.color
})

/** Цвет итога: profit бывает отрицательным, ноль — нейтральный */
export const profitClass = (profit) => {
  const value = Number(profit) || 0
  if (value > 0) return 'text-accent-primary'
  if (value < 0) return 'text-red-400'
  return 'text-text-secondary'
}

/** «+25 000» / «−10 000»: знак рисуем сами, в profit его нет */
export const formatSigned = (value) => {
  const number = Number(value) || 0
  const sign = number > 0 ? '+' : number < 0 ? '\u2212' : ''
  return `${sign}${Math.abs(number).toLocaleString('ru-RU')}`
}

/** Проценты бэкенда округлены до целого — остаётся только прижать к 0–100 */
export const clampPct = (pct) => Math.min(100, Math.max(0, Number(pct) || 0))

/** Ошибки статистики и истории игр (docs/team-stats-api.md, §6) */
export const statsErrorText = (code, t) => {
  const messages = {
    stats_hidden: t('team.stats.err.hidden', 'Банда скрыла свою статистику'),
    rate_limited: t('api.rateLimited'),
    not_found: t('team.err.notFound', 'Банда не найдена'),
    game_not_found: t('team.stats.err.gameNotFound', 'Игра не найдена или она не командная'),
    invalid_period: t('team.stats.err.invalidPeriod', 'Неверный период'),
    invalid_kind: t('team.stats.err.invalidKind', 'Неизвестный режим')
  }
  return messages[code] || t('team.err.default', 'Что-то пошло не так, попробуй ещё раз')
}
